import { Conversation as ConversationClient, Media, Message, MessageBuilder, Paginator } from "@twilio/conversations";
import {
    Conversation,
    MessagesDirection,
    SendEmailMessageOptions,
    SendMediaMessageOptions,
    SendTextMessageOptions
} from "./Conversation";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { convertHTMLToPlainText } from "~/modules/Conversation/ConversationUtils";

export class ConversationImpl implements Conversation {
    readonly #conversation: ConversationClient;

    readonly #logger: Logger;

    constructor(ctx: ContextManager, conversation: ConversationClient) {
        this.#conversation = conversation;
        this.#logger = getLogger(ctx)(LoggerName.Conversations);
    }

    get sid() {
        return this.#conversation.sid;
    }

    get conversation() {
        return this.#conversation;
    }

    
    async sendMessage(
        messageOptions: SendTextMessageOptions | SendEmailMessageOptions | SendMediaMessageOptions
    ): Promise<number | null> {
        if (!this.sid) {
            const errorMsg = "sendMessage: no valid conversation exists for sendMessage action.";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const isHTMLMessage = Boolean("htmlBody" in messageOptions && messageOptions.htmlBody);
        let messageHasContent = false;

        try {
            let plainTextBody: string = "";
            // Converting HTML messageOptions to plain text so there is Body present, in case not passed otherwise
            if (isHTMLMessage) {
                if (!("body" in messageOptions && messageOptions.body)) {
                    if ("htmlBody" in messageOptions) {
                        plainTextBody = plainTextBody || convertHTMLToPlainText(messageOptions.htmlBody);
                    }
                }
            }

            let preparedMessage: MessageBuilder;

            if ("body" in messageOptions) {
                preparedMessage = this.conversation.prepareMessage().setBody(messageOptions.body);
                messageHasContent = messageOptions.body.length > 0;
            } else {
                preparedMessage = this.conversation.prepareMessage().setBody(plainTextBody);
                messageHasContent = plainTextBody.length > 0;
            }

            
            if (messageOptions.attachedFiles && messageOptions.attachedFiles.length !== 0) {
                messageOptions.attachedFiles.forEach((file: File) => {
                    const formData = new FormData();
                    formData.append(file.name, file);
                    preparedMessage.addMedia(formData);
                    messageHasContent = true;
                });
            }

            if (messageOptions.messageAttributes) {
                preparedMessage = preparedMessage.setAttributes(messageOptions.messageAttributes);
            }

            
            if ("subject" in messageOptions && messageOptions.subject) {
                preparedMessage = preparedMessage.setSubject(messageOptions.subject);
                messageHasContent = true;
            }

            if ("htmlBody" in messageOptions && messageOptions.htmlBody && messageOptions.htmlBody !== "") {
                preparedMessage = preparedMessage.setEmailBody("text/html", {
                    media: messageOptions.htmlBody,
                    contentType: "text/html"
                });
                messageHasContent = true;
            }

            if (plainTextBody && plainTextBody !== "") {
                preparedMessage = preparedMessage.setEmailBody("text/plain", {
                    media: plainTextBody,
                    contentType: "text/plain"
                });
                messageHasContent = true;
            }

            if (messageHasContent) {
                return await preparedMessage.build().send();
            }
            const errorMsg = "sendMessage: Cannot send empty message";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        } catch (e) {
            const errorMsg = `sendMessage: failed to send message: ${e.message}`;
            this.#logger.error(e);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }

    sendTyping(): void {
        this.#conversation.typing();
    }

    downloadMedia(media: Media): string {
        return "";
    }

    async getMessages(
        pageSize?: number,
        anchorMessageIndex?: number,
        direction?: MessagesDirection
    ): Promise<Paginator<Message>> {
        return this.#conversation.getMessages(pageSize, anchorMessageIndex, direction);
    }
}
